import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

function formatRelative(epochSeconds: number): string {
  const deltaMs = Date.now() - epochSeconds * 1000;
  const minutes = Math.round(deltaMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function usd(n: number): string {
  return `$${n.toFixed(2)}`;
}

/**
 * The live status/monitoring dashboard (docs/347-ADMIN-ALERTS-AND-MONITORING.md v0 §7)
 * — current metric headroom + up/down + recent alert history + the auto per-client
 * Cloudflare cost widget ("no CLI needed"). Three independent queries, each with its
 * own loading/error state — a slow/failing Cloudflare call never blocks the rest of
 * the page.
 */
export function MonitoringPage() {
  const { client, token } = useAuth();

  const monitoringQuery = useQuery({
    queryKey: ["admin-monitoring"],
    queryFn: () => client!.adminMonitoring(token!),
    enabled: !!client && !!token,
    refetchInterval: 60_000,
  });
  const eventsQuery = useQuery({
    queryKey: ["admin-alert-events"],
    queryFn: () => client!.adminAlertEvents(token!, { limit: 50 }),
    enabled: !!client && !!token,
  });
  const costQuery = useQuery({
    queryKey: ["admin-cost"],
    queryFn: () => client!.adminCost(token!),
    enabled: !!client && !!token,
  });

  const monitoring = monitoringQuery.data;
  const events = eventsQuery.data ?? [];
  const cost = costQuery.data;

  return (
    <>
      <MainCard
        title="Status"
        sx={{ mb: 2 }}
        secondary={
          <Button size="small" variant="outlined" color="secondary" onClick={() => monitoringQuery.refetch()}>
            Refresh
          </Button>
        }
      >
        {monitoringQuery.isError ? <Alert severity="error">{(monitoringQuery.error as Error).message}</Alert> : null}
        {monitoringQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : monitoring ? (
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                size="small"
                color={monitoring.heartbeat.stale ? "error" : monitoring.heartbeat.at == null ? "default" : "success"}
                label={
                  monitoring.heartbeat.at == null
                    ? "Cron hasn't run yet"
                    : monitoring.heartbeat.stale
                      ? "Cron heartbeat stale"
                      : "Cron healthy"
                }
              />
              <Typography variant="body2" color="text.secondary">
                {monitoring.heartbeat.at != null
                  ? `Last completed tick ${formatRelative(monitoring.heartbeat.at)}`
                  : "No completed tick recorded on this deployment yet"}
                {" · checked every "}
                {Math.round(monitoring.cronCadenceSeconds / 60)}m
              </Typography>
            </Box>

            {monitoring.metrics.length === 0 ? (
              <Typography color="text.secondary" sx={{ mb: 0 }}>
                No enabled rules with a current sample. Create/enable a rule on the Alerts page.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {monitoring.metrics.map((m) => (
                  <Box key={m.ruleId}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {m.label} {m.firing ? <Chip size="small" color={m.severity === "critical" ? "error" : "warning"} label="firing" sx={{ ml: 1 }} /> : null}
                      </Typography>
                      <Typography color={m.breaching ? "error" : "text.secondary"}>
                        {m.value} (threshold {m.threshold})
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={m.threshold > 0 ? Math.min(100, (m.value / m.threshold) * 100) : m.breaching ? 100 : 0}
                      color={m.breaching ? "error" : "success"}
                    />
                  </Box>
                ))}
              </Stack>
            )}

            {monitoring.unavailableRuleIds.length > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                {monitoring.unavailableRuleIds.length} rule(s) unavailable this check (e.g. a Cloudflare metric with
                no API token configured).
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </MainCard>

      <MainCard title="Recent alert history" sx={{ mb: 2 }}>
        {eventsQuery.isError ? <Alert severity="error">{(eventsQuery.error as Error).message}</Alert> : null}
        {eventsQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : events.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            No alerts have fired yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {events.map((e) => (
              <Box key={e.id} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {e.ruleLabel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {e.message}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Chip size="small" color={e.status === "open" ? (e.severity === "critical" ? "error" : "warning") : "success"} label={e.status} />
                  <Typography variant="body2" color="text.secondary">
                    {formatRelative(e.openedAt)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </MainCard>

      <MainCard title="Per-client cost (Cloudflare, trailing 30 days)">
        {costQuery.isError ? <Alert severity="error">{(costQuery.error as Error).message}</Alert> : null}
        {costQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : cost && cost.available ? (
          <Stack spacing={1}>
            <Typography variant="h4">{usd(cost.report.totalUsd)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {cost.since} – {cost.until}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {cost.report.lines
                .filter((l) => l.usdCost > 0)
                .map((l) => (
                  <Box key={l.metric} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">{l.metric}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usd(l.usdCost)}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          </Stack>
        ) : (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            {cost && !cost.available ? cost.reason : "Cost engine pending."}
          </Typography>
        )}
      </MainCard>
    </>
  );
}
